import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ROOT_URL from "../../../Utilities/apiconfig";
import ContactMarquee from "../ContactMarquee";

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${ROOT_URL}/api/medicos`);
        setDoctors(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch doctors data");
        setLoading(false);
      }
    };
    fetchDoctors();
  }, []);

  if (loading) return (
    <div className="text-center py-5" role="status" aria-live="polite">
      <div className="spinner-border" aria-label="Loading doctors..."></div>
      <p className="mt-2">Loading doctors...</p>
    </div>
  );
  
  if (error) return (
    <div className="text-center py-5 text-danger" role="alert">
      <p>{error}</p>
    </div>
  );

  const featuredDoctor = doctors.length > 0 ? doctors[0] : null;
  const displayedDoctors = doctors.slice(1);

  return (
    <>
      <Helmet>
        <title>Columbiaa Hospital | Best Multi-Speciality Hospital in Kasturi Nagar Bangalore - Expert Doctors</title>
        <meta name="description" content="Columbiaa Hospital is the best multi-speciality hospital in Kasturi Nagar, Bangalore. Meet our expert doctors including top neurosurgeons, experienced physicians, and skilled surgeons. Book appointments online for world-class healthcare." />
        <meta name="keywords" content="hospital, best hospital, multi-speciality hospital, doctors, neurosurgeon, top doctor, brain surgeon, spine surgeon, neurosurgeon in Bangalore, hospital in Kasturi Nagar, Columbiaa Hospital, expert doctors, medical treatment, healthcare, Bangalore hospital, specialist doctor, book appointment, neurosurgeon Bangalore, top neurosurgeon India, brain surgery Bangalore, spine surgery Bangalore" />
        <meta name="author" content="Columbiaa Hospital" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Columbiaa Hospital | Best Multi-Speciality Hospital in Kasturi Nagar Bangalore - Expert Doctors" />
        <meta property="og:description" content="Meet expert doctors at Columbiaa Hospital, the top multi-speciality hospital in Kasturi Nagar, Bangalore. Best neurosurgeons and medical specialists available for appointments." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://columbiaahospital.com/doctors" />
        <meta property="og:site_name" content="Columbiaa Hospital" />
        <meta property="og:locale" content="en_IN" />
        <link rel="canonical" href="https://columbiaahospital.com/doctors" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalOrganization",
              "name": "Columbiaa Hospital",
              "url": "https://columbiaahospital.com",
              "logo": "https://columbiaahospital.com/assets/images/zclogo.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-8548979899",
                "contactType": "appointments",
                "areaServed": "IN",
                "availableLanguage": ["English", "Hindi", "Kannada"]
              },
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Building No 06, 5th Main Rd, B Channasandra, OMBR Layout",
                "addressLocality": "Bengaluru",
                "addressRegion": "Karnataka",
                "postalCode": "560043",
                "addressCountry": "IN"
              },
              "sameAs": [
                "https://www.facebook.com/share/1BFLVsneD2/",
                "https://x.com/columbiaahosp",
                "https://www.linkedin.com/in/columbiaa-hospital-kasturi-nagar-bengaluru-98aa37323/",
                "https://www.instagram.com/columbiaahospital/",
                "https://www.youtube.com/@columbiaahospital"
              ]
            }
          `}
        </script>
      </Helmet>

      <section
        className="page-title py-5"
        style={{
          backgroundImage: "url(assets/images/background/page-title-2.jpg)",
          overflowX: "hidden",
        }}
        aria-label="Meet Our Doctors - Columbiaa Hospital, Kasturi Nagar, Bangalore"
      >
        <div
          className="pattern-layer"
          style={{
            backgroundImage: "url(assets/images/shape/pattern-35.png)",
          }}
          aria-hidden="true"
        ></div>
        <div className="container">
          <div className="row justify-content-center">
            <div
              className="col-12 col-md-10 text-center"
              data-aos="fade-right"
            >
              <div className="title-box my-4 my-md-0">
                <h1 className="display-4" aria-label="Our Doctors">Our Doctors</h1>
                <p className="lead" aria-label="Columbiaa Hospital - Your Health Care Partner">
                  Columbiaa Hospital – Your Health Care Partner in Kasturi Nagar, Bangalore.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        style={{ marginTop: "-80px" }}
        data-aos="fade-right"
        aria-label="Columbiaa Hospital Doctors Introduction"
      >
        <div className="sec-title centred mt-5 pt-5">
          <span className="top-title" title="Meet Our Doctors" aria-label="Experienced and skilled medical professionals">
            Meet Our Doctors
          </span>
          <h2 
            style={{ fontSize: "44px" }} 
            title="Experienced & Skilled Medical Professionals"
            aria-label="Experienced and Skilled Medical Professionals at Columbiaa Hospital"
          >
            Experienced & Skilled Medical Professionals
          </h2>
        </div>
      </section>

      {featuredDoctor && (
        <section
          className="container my-5 p-4 rounded"
          aria-label={`Featured Doctor at Columbiaa Hospital: Dr. ${featuredDoctor.name}`}
          title={`Featured Doctor: Dr. ${featuredDoctor.name}`}
          style={{ backgroundColor: "#f0f4f8" }}
          role="article"
        >
          <div className="row align-items-center">
            <div className="col-md-5" data-aos="fade-down-right">
              <img
                src={`${ROOT_URL}/uploads/medicos/${featuredDoctor.image}`}
                alt={`Dr. ${featuredDoctor.name} - ${featuredDoctor.designation} at Columbiaa Hospital, Kasturi Nagar, Bangalore`}
                title={`Dr. ${featuredDoctor.name}'s profile picture - ${featuredDoctor.qualification}`}
                style={{ width: "100%", borderRadius: "20px" }}
                loading="lazy"
              />
            </div>
            <div className="col-md-7" data-aos="fade-left">
              <h3 
                style={{ fontSize: "32px", fontWeight: "bold" }} 
                title={`Doctor Name: Dr. ${featuredDoctor.name}`}
                aria-label={`Dr. ${featuredDoctor.name}`}
              >
                Dr. {featuredDoctor.name}
              </h3>
              <h6 
                className="my-2" 
                style={{ color: "#000", fontWeight: "normal" }} 
                title={`Qualification: Dr. ${featuredDoctor.qualification}`}
                aria-label={`Qualification: ${featuredDoctor.qualification}`}
              >
                {featuredDoctor.qualification}
              </h6>
              <h5 
                className="pt-2" 
                style={{ color: "#000", fontWeight: "bold" }} 
                title={`Designation: Dr. ${featuredDoctor.designation}`}
                aria-label={`Designation: ${featuredDoctor.designation}`}
              >
                {featuredDoctor.designation}
              </h5>
            
              <h6  
                style={{ color: "#000", fontWeight: "normal" }} 
                title={`Experience: Dr. ${featuredDoctor.experience} years`}
                aria-label={`Experience: ${featuredDoctor.experience} years`}
              >
                Experience :  {featuredDoctor.experience} years
              </h6>
              <p 
                style={{ fontSize: "1rem", color: "#777", marginTop: "10px" }} 
                title="Doctor Bio"
                aria-label={`Biography of Dr. ${featuredDoctor.name}`}
              >
                {featuredDoctor.bio}
              </p>
              <button
                className="btn btn-success mt-3"
                style={{borderRadius: "8px", padding: "8px 16px" }}
                onClick={() => navigate(`/bookappointments/${featuredDoctor._id}`)}
                aria-label={`Book appointment with Dr. ${featuredDoctor.name} at Columbiaa Hospital`}
                title={`Book appointment with Dr. ${featuredDoctor.name}`}
              >
                Book Appointment
              </button>
            </div>
          </div>
        </section>
      )}
      
      <ContactMarquee/>
      
      <section
        className="py-5"
        style={{ backgroundColor: "#eef2f7" }}
        aria-label="Columbiaa Hospital Doctors Listing Section"
        title="All Doctors at Columbiaa Hospital"
      >
        <div className="container">
          <h2 className="text-center mb-4" aria-label="All Our Doctors">
            All Our Doctors
          </h2>
          <div className="row">
            {displayedDoctors.map((doctor) => (
              <article
                key={doctor._id}
                className="col-lg-3 col-md-4 col-sm-6 mb-4"
                aria-label={`Doctor Card for Dr. ${doctor.name} at Columbiaa Hospital`}
                title={`Dr. ${doctor.name} - ${doctor.designation}`}
              >
                <div className="class_box border border-success shadow bg-white rounded p-3">
                  {/* Top Section: Image + Name + Designation */}
                  <header className="d-flex align-items-center">
                    {/* Left: Doctor Image */}
                    <div
                      className="class_Img"
                      data-aos="fade-down-right"
                      style={{
                        width: "35%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "3px solid teal",
                        borderRadius: "50%",
                      }}
                    >
                      <img
                        src={`${ROOT_URL}/uploads/medicos/${doctor.image}`}
                        alt={`Dr. ${doctor.name} - ${doctor.designation} at Columbiaa Hospital, Kasturi Nagar`}
                        title={`Profile photo of Dr. ${doctor.name}`}
                        style={{
                          width: "100%",
                          height: "10vh",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                        loading="lazy"
                      />
                    </div>

                    {/* Right: Doctor Name & Designation */}
                    <div className="ps-3" data-aos="fade-left" style={{ width: "70%" }}>
                      <h6
                        style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}
                        title={`Doctor Name: Dr. ${doctor.name}`}
                        aria-label={`Dr. ${doctor.name}`}
                      >
                        {doctor.name}
                      </h6>
                      <h6 
                        className="pb-2" 
                        style={{fontSize: "12px", color: "#000", fontWeight: "normal" }} 
                        title={`Qualification: Dr. ${doctor.qualification}`}
                        aria-label={`Qualification: ${doctor.qualification}`}
                      >
                        {doctor.qualification}
                      </h6>
                      <h6
                        style={{ fontSize: "14px", fontWeight: "normal", color: "#000", marginBottom: "0" }}
                        title={`Designation: Dr. ${doctor.designation}`}
                        aria-label={`Designation: ${doctor.designation}`}
                      >
                        {doctor.designation}
                      </h6>
                    </div>
                  </header>

                  {/* Bottom Section: Bio & Button */}
                  <div className="mt-3 text-center">
                    <h6 
                      className="pb-2" 
                      style={{ color: "#000", fontWeight: "normal" }} 
                      title={`Experience: Dr. ${doctor.experience} years`}
                      aria-label={`Experience: ${doctor.experience} years`}
                    >
                      Experience :  {doctor.experience} years
                    </h6>
                    <p 
                      style={{ fontSize: "14px", color: "#777", marginBottom: "8px" }} 
                      title="Doctor Bio"
                      aria-label={`Biography of Dr. ${doctor.name}`}
                    >
                      {doctor.bio}
                    </p>
                    <button
                      className="btn btn-success mt-2"
                      style={{
                        borderRadius: "8px",
                        padding: "8px 12px",
                        width: "100%",
                      }}
                      onClick={() => navigate(`/bookappointments/${doctor._id}`)}
                      aria-label={`Book appointment with Dr. ${doctor.name} at Columbiaa Hospital`}
                      title={`Book appointment with Dr. ${doctor.name}`}
                      disabled={
                        doctor.name === "Dr S Arivazhagan" ||
                        doctor.name === "Dr. Munireddy M V" ||
                        doctor.name === "Dr Ramanjanayalu" ||
                        doctor.name === "Dr Anagha Auradkar" ||
                        doctor.name === "Dr. Chandra Shekar M" ||
                        doctor.name === "Dr Volga Joshy (PT)" ||
                        doctor.name === "Dr Swetha" ||
                        doctor.name === "Dr Swetha" 
                      }
                    >
                      Book Appointment
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Doctors;
